import React, { useCallback } from 'react';
import { Table, Button, Loader, Alert, Text } from '@mantine/core';
import {
    IconCirclePlus,
    IconDotsVertical,
    IconPencil,
    IconTrash,
} from '@tabler/icons-react';
import { GET_COUNTRY_VAT_CONFIGS } from '../../apollo/queries/vatConfig';
import { DELETE_COUNTRY_VAT_CONFIG } from '../../apollo/mutations/vatConfig';
import { ConfirmDeleteModal, TableActions } from '../common';
import { CountryVatConfigFormModal } from './CountryVatConfigFormModal';
import { CountryVatConfigRow } from './vatConfigTypes';
import { useVatConfigCrud } from './useVatConfigCrud';

type CountryVatConfigsQueryData = {
    countryVatConfigs: CountryVatConfigRow[];
};

export function CountryVatConfigManager() {
    const getRows = useCallback(
        (data: CountryVatConfigsQueryData | undefined) =>
            data?.countryVatConfigs ?? [],
        [],
    );

    const getDeleteSuccessCopy = useCallback(
        (row: CountryVatConfigRow) => ({
            title: 'Country VAT rate deleted',
            message: `${row.countryName} has been removed.`,
        }),
        [],
    );

    const {
        rows: configs,
        loading,
        error,
        deleting,
        selectedConfig,
        formModalOpened,
        formModalHandlers,
        deleteModalOpened,
        deleteModalHandlers,
        handleAdd,
        handleEdit,
        handleDeleteRequest,
        handleDeleteConfirm,
    } = useVatConfigCrud<CountryVatConfigRow, CountryVatConfigsQueryData>({
        query: GET_COUNTRY_VAT_CONFIGS,
        deleteMutation: DELETE_COUNTRY_VAT_CONFIG,
        getRows,
        getDeleteSuccessCopy,
        deleteErrorMessage: 'Failed to delete country VAT rate',
    });

    if (loading) {
        return (
            <div className="flex justify-center p-6">
                <Loader size="sm" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert color="red" title="Error loading country VAT rates">
                {error.message}
            </Alert>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <Text size="sm" c="dimmed">
                    VAT rate applied based on the store&apos;s country. Falls
                    back to the global VAT % above when a country has no
                    configured rate.
                </Text>
                <Button
                    type="button"
                    size="xs"
                    rightSection={<IconCirclePlus size={16} />}
                    onClick={handleAdd}
                >
                    Add Country
                </Button>
            </div>

            <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Country Code</Table.Th>
                        <Table.Th>Country Name</Table.Th>
                        <Table.Th>VAT %</Table.Th>
                        <Table.Th w={60}>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {configs.length === 0 ? (
                        <Table.Tr>
                            <Table.Td colSpan={4}>
                                <Text size="sm" c="dimmed" ta="center" py="md">
                                    No country VAT rates configured yet.
                                </Text>
                            </Table.Td>
                        </Table.Tr>
                    ) : (
                        configs.map(config => (
                            <Table.Tr key={config.id}>
                                <Table.Td>{config.countryCode}</Table.Td>
                                <Table.Td>{config.countryName}</Table.Td>
                                <Table.Td>{config.vatPercent}%</Table.Td>
                                <Table.Td>
                                    {deleting &&
                                    selectedConfig?.id === config.id ? (
                                        <Loader size="xs" />
                                    ) : (
                                        <TableActions
                                            row={config}
                                            options={[
                                                {
                                                    label: 'Edit',
                                                    icon: (
                                                        <IconPencil
                                                            className="text-primary"
                                                            size={16}
                                                        />
                                                    ),
                                                    onClick: () =>
                                                        handleEdit(config),
                                                    isShown: true,
                                                },
                                                {
                                                    label: 'Delete',
                                                    icon: (
                                                        <IconTrash
                                                            color="red"
                                                            size={16}
                                                        />
                                                    ),
                                                    onClick: () =>
                                                        handleDeleteRequest(
                                                            config,
                                                        ),
                                                    isShown: true,
                                                },
                                            ]}
                                        >
                                            <Button
                                                type="button"
                                                variant="subtle"
                                                className="text-primary rounded-md p-1 w-min h-min"
                                            >
                                                <IconDotsVertical size={16} />
                                            </Button>
                                        </TableActions>
                                    )}
                                </Table.Td>
                            </Table.Tr>
                        ))
                    )}
                </Table.Tbody>
            </Table>

            <CountryVatConfigFormModal
                opened={formModalOpened}
                onClose={formModalHandlers.close}
                countryVatConfig={selectedConfig}
            />
            <ConfirmDeleteModal
                opened={deleteModalOpened}
                close={deleteModalHandlers.close}
                onConfirm={handleDeleteConfirm}
                subject="Country VAT Rate"
                message={
                    selectedConfig
                        ? `Are you sure you want to delete the VAT rate for ${selectedConfig.countryName}?`
                        : undefined
                }
                loading={deleting}
            />
        </div>
    );
}
